import React, { FC, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { PersonaProfileResponse } from '../api/types';

interface ProfilePickerProps {
  /** All available profiles to pick from. */
  profiles: PersonaProfileResponse[];

  /** The currently selected profile ID (or null if none selected yet). */
  selectedProfileId: string | undefined| null;

  /** Called when a new profile is selected from the dropdown. */
  onChange: (profileId: string) => void;

  /** (Optional) Label to show if no profile is selected. Defaults to 'Select Profile'. */
  placeholder?: string;
}

/**
 * A reusable dropdown to select one of the user's profiles.
 * Uses Headless UI Menu for the dropdown logic & transitions.
 */
export const ProfilePicker: FC<ProfilePickerProps> = ({
    profiles,
    selectedProfileId,
    onChange,
    placeholder = 'Select Profile',
  }) => {
  // Find the currently selected profile to display its name in the button
  const selectedProfile = profiles.find((p) => p.id === selectedProfileId);

  return (
    <Menu as="div" className="relative inline-block text-left">
      {/* The button that shows the current selection */}
      <div>
        <MenuButton
          className="inline-flex w-full justify-center gap-x-1.5 rounded-md
                     bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm
                     ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          {selectedProfile?.displayName ?? placeholder}
          <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
        </MenuButton>
      </div>

      {/* The transition that toggles the dropdown open/close */}
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
          <div className="py-1">
            {profiles.map((profile) => {
              const isUnassigned = profile.personaId === null;

              return (
                <Menu.Item key={profile.id}>
                  {({ active }) => (
                    <button
                      onClick={() => onChange(profile.id)}
                      disabled={isUnassigned}
                      className={`
                        flex w-full items-center gap-3 px-4 py-2 text-sm
                        ${isUnassigned ? 'cursor-not-allowed opacity-50' : ''}
                        ${active && !isUnassigned ? 'bg-gray-50' : ''}
                        ${isUnassigned ? 'bg-gray-50' : ''}
                      `}
                    >
                      {profile.url ? (
                        <img
                          src={profile.url}
                          alt=""
                          className={`h-8 w-8 rounded-full object-cover ${isUnassigned ? 'grayscale' : ''}`}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gray-200" />
                      )}
                      <div className="flex flex-col items-start">
                        <span className={isUnassigned ? 'text-gray-400' : 'text-gray-700'}>
                          {profile.displayName}
                        </span>
                        {isUnassigned && (
                          <span className="text-xs text-gray-400">No face assigned</span>
                        )}
                      </div>
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default ProfilePicker;
